/* eslint-disable */
"use client";

import { authService } from '@/services/auth.service';

const PERSONALIZATION_KEY = 'dw_personalization_enabled';
const USER_KEY_PREFIX = 'dw_personalization_user:';

/**
 * 로컬스토리지에서 개인화 설정 상태를 가져옵니다
 */
export function getPersonalizationEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const stored = localStorage.getItem(PERSONALIZATION_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
}

/**
 * 사용자별 개인화 설정 상태를 가져옵니다
 */
export function getPersonalizationEnabledFor(userId: string | number): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = `${USER_KEY_PREFIX}${String(userId)}`;
    const stored = localStorage.getItem(key);
    if (stored === null) {
      // 사용자별 설정이 없으면 글로벌 키(과거 버전 호환) 참조
      return getPersonalizationEnabled();
    }
    return stored === 'true';
  } catch {
    return false;
  }
}

/**
 * 로컬스토리지에 개인화 설정 상태를 저장합니다
 */
export function setPersonalizationEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(PERSONALIZATION_KEY, enabled ? 'true' : 'false');
  } catch {
    // 로컬스토리지 사용 불가능한 환경에서는 무시
  }
}

/**
 * 사용자별 개인화 설정 상태를 저장합니다
 */
export function setPersonalizationEnabledFor(userId: string | number, enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    const key = `${USER_KEY_PREFIX}${String(userId)}`;
    localStorage.setItem(key, enabled ? 'true' : 'false');
  } catch {
    // ignore
  }
}

/**
 * 로컬스토리지에서 개인화 설정을 제거합니다
 */
export function clearPersonalizationSetting(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(PERSONALIZATION_KEY);
  } catch {
    // 로컬스토리지 사용 불가능한 환경에서는 무시
  }
}

/**
 * 로그인 상태와 개인화 데이터 존재 여부를 확인하여 개인화 기능 활성화 가능 여부를 판단합니다
 */
export async function checkPersonalizationAvailable(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  
  try {
    // 1) 로그인 상태 확인
    const sessionData = await authService.getSession();
    // To-Be 형식 처리
    if (sessionData?.code !== "SUCCESS" || !sessionData?.data) {
      return false;
    }
    
    // 2) 개인화 데이터 존재 여부 확인
    const personalizationData = await authService.getPersonalization();
    // To-Be 형식 처리
    if (personalizationData?.code !== "SUCCESS" || !personalizationData?.data) {
      return false;
    }
    
    const data = personalizationData.data ?? {};
    const hasJob = !!(String(data?.job || '').trim());
    const hasInterests = !!(String(data?.interests || '').trim());
    
    return hasJob || hasInterests;
  } catch {
    return false;
  }
}

/**
 * 개인화 설정을 초기화합니다 (로그아웃 시 호출)
 */
export function initializePersonalizationOnLogout(): void {
  // 로그아웃 시에도 사용자별 개인화 설정은 보존합니다 (요구사항)
  // 과거 글로벌 키만 사용하던 값을 지우지 않고 그대로 둡니다.
}

/**
 * 로그인 시 개인화 설정을 초기화하고 로컬스토리지 값에 따라 설정합니다
 */
export async function initializePersonalizationOnLogin(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  
  try {
    // 세션에서 사용자 식별
    const sessionData = await authService.getSession();
    // To-Be 형식 처리
    const userId = sessionData?.code === "SUCCESS" && sessionData?.data?.id ? String(sessionData.data.id) : null;

    // 개인화 데이터 사용 가능 여부 확인 (직무/관심사 존재)
    const available = await checkPersonalizationAvailable();
    if (!available) {
      // 개인화 데이터가 없으면 전역 활성화는 false로 두되, 저장된 사용자별 설정은 보존
      (window as any).__dw_personalize_active__ = false;
      window.dispatchEvent(new CustomEvent('dw:personalize_toggle', { detail: false }));
      return false;
    }

    // 사용자별 로컬스토리지에서 설정 가져오기
    const userPreference = userId ? getPersonalizationEnabledFor(userId) : getPersonalizationEnabled();

    // 전역 상태 설정 및 브로드캐스트
    (window as any).__dw_personalize_active__ = userPreference;
    window.dispatchEvent(new CustomEvent('dw:personalize_toggle', { detail: userPreference }));

    return userPreference;
  } catch {
    return false;
  }
}
